import { graphql } from "@repo/graphql-types";
import HelpCircle from "@repo/icons/default/HelpCircle";
import Left from "@repo/icons/default/Left";
import {
  createFileRoute,
  useRouteContext,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { useGraphQL } from "@/hooks/use-graphql";
import { Page } from "@/components/ui/layout/page/page";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { PaymentsListContainer } from "@/components/payment/patient/patient-payments-list-container";
import { patientPaymentsPageSearchSchema } from "@/lib/form-schemas/patient-payments-page-search-schema";
import {
  zonedDate,
  getDateFromToday,
  formatDateAndTimeIgnoringTimezone,
} from "@/lib/helpers/date";

import type { JSX } from "react";

const PatientsPaymentsQuery = graphql(`
  query PatientsPaymentsQuery(
    $intervalStart: String!
    $intervalEnd: String!
    $codClinica: Int
    $origins: [String!]
    $paymentMethods: [String!]
    $status: [String!]
  ) {
    patientPayments: ExpensumGetPayments(
      arg1: {
        intervalStart: $intervalStart
        intervalEnd: $intervalEnd
        codClinica: $codClinica
        origin: $origins
        paymentMethod: $paymentMethods
        status: $status
      }
    ) {
      totalAmount
      ...PatientPaymentsListFragment
    }
  }
`);

const today = zonedDate(new Date());
const last7Days = getDateFromToday(-6); // menos um dia pois conta o dia de hoje

const PatientPaymentsPage = (): JSX.Element => {
  const searchParams = useSearch({
    from: "/settings/payments/patient-payments/",
  });
  const { user } = useRouteContext({ from: "__root__" });

  const buildInterval = (): { intervalStart: string; intervalEnd: string } => {
    const intervalStart = searchParams.startDate ?? searchParams.endDate ?? last7Days;
    const intervalEnd = searchParams.endDate ?? searchParams.startDate ?? today;

    return {
      intervalStart: formatDateAndTimeIgnoringTimezone(
        intervalStart,
        undefined,
        "yyyy-MM-dd",
      ),
      intervalEnd: formatDateAndTimeIgnoringTimezone(
        intervalEnd,
        undefined,
        "yyyy-MM-dd",
      ),
    };
  };

  const { intervalStart, intervalEnd } = buildInterval();

  const router = useRouter();

  const queryData = useGraphQL(PatientsPaymentsQuery, {
    intervalStart,
    intervalEnd,
    codClinica: user.codClinica,
    origins: searchParams.origins,
    paymentMethods: searchParams.paymentMethods,
    status: searchParams.status,
  });

  const { data } = queryData;

  const navigateBack = (): void => {
    const backSteps = searchParams.back ?? 0;
    const historySteps = -1 - backSteps;

    router.history.go(historySteps);
  };

  const handleClickAbout = (): void => {
    void router.navigate({
      to: "/settings/payments/patient-payments/about-payments",
    });
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton onClick={navigateBack} icon={Left} align="start" />
        <HeaderTitle title="Pagamentos" />
        <HeaderButton align="end" icon={HelpCircle} onClick={handleClickAbout} />
      </HeaderRoot>

      <Page>
        <AsyncDataWrapper {...queryData}>
          <PaymentsListContainer
            data={data}
            intervalStart={intervalStart}
            intervalEnd={intervalEnd}
          />
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/payments/patient-payments/")({
  component: PatientPaymentsPage,
  validateSearch: patientPaymentsPageSearchSchema,
});
